import { useState } from "react";
import { CreditCard, Smartphone, ShieldCheck, Search, X } from "lucide-react";
import { Hero3D } from "@/components/Hero3D";
import { WinTicker } from "@/components/WinTicker";
import { GameCard } from "@/components/GameCard";
import { GAMES, CATEGORIES, gameCategory } from "@/lib/games";
import { useAuth } from "@/context/AuthContext";
import { PromoRedeem } from "@/components/PromoRedeem";

const FEATURES = [
  { Icon: CreditCard, t: "Bank kartı ilə depozit", d: "Köçürməni edin, qəbzi yükləyin. Minimum 15 ₼." },
  { Icon: Smartphone, t: "m10 və karta çıxarış", d: "Minimum 25 ₼, gündəlik 5 000 ₼-dək." },
  { Icon: ShieldCheck, t: "Təhlükəsiz hesab", d: "Şifrələnmiş məlumatlar və e-poçtla bərpa." },
];

const STEPS = [
  ["01", "Qeydiyyat", "E-poçt və şifrə ilə bir dəqiqədə hesab yaradın."],
  ["02", "Depozit", "Karta köçürün, qəbzi yükləyin, balans təsdiqdən sonra artır."],
  ["03", "Oyna", "Rulet, slotlar, Aviator və digər oyunlardan seçin."],
  ["04", "Çıxarış", "Qazancınızı m10 və ya bank kartına çıxarın."],
];

const HowTo = () => (
  <section id="how" className="max-w-7xl mx-auto px-4 sm:px-8 pt-24" data-testid="how-section">
    <p className="text-sm uppercase tracking-[0.2em] text-[#D4AF37]">Başlamaq asandır</p>
    <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-2 mb-10">4 addımda oyuna başlayın</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
      {STEPS.map(([n, t, d]) => (
        <div key={n} className="bg-[#0a0a0a] p-8 transition-colors hover:bg-[#111]">
          <p className="font-display font-black text-4xl text-[#D4AF37]/30">{n}</p>
          <h3 className="font-display font-semibold text-lg mt-4">{t}</h3>
          <p className="text-zinc-400 text-sm mt-2">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

export default function Home() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const list = GAMES.filter((g) => (cat === "all" || gameCategory(g) === cat) && g.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div data-testid="home-page">
      {user === false && <Hero3D />}
      <WinTicker />
      {user && <div className="max-w-7xl mx-auto px-3 sm:px-8 pt-8"><PromoRedeem compact withWallet /></div>}
      <section id="games" className={`max-w-7xl mx-auto px-3 sm:px-8 ${user ? "py-12" : "py-24"}`}>
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#D4AF37]">{user ? "Lobbi" : "Kazino lobbisi"}</p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-2">{user ? "Bütün oyunlar" : "Slotlar, rulet və canlı oyunlar"}</h2>
          </div>
          <p className="text-zinc-500 text-sm hidden sm:block" data-testid="games-count">{list.length} oyun</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="game-categories">
          {CATEGORIES.map(([k, l]) => (
            <button key={k} onClick={() => setCat(k)} className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${cat === k ? "bg-[#D4AF37] text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`} data-testid={`category-${k}`}>
              {l} <span className="opacity-60 ml-1">{k === "all" ? GAMES.length : GAMES.filter((g) => gameCategory(g) === k).length}</span>
            </button>
          ))}
        </div>
        <div className="relative mb-8 max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input className="jp-input !pl-11 !pr-11" placeholder="Oyun axtar..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="game-search-input" />
          {q && <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white" data-testid="game-search-clear"><X size={18} /></button>}
        </div>
        {list.length === 0 && <p className="text-zinc-500 py-12 text-center" data-testid="game-search-empty">{q ? `"${q}" adlı oyun tapılmadı` : "Bu kateqoriyada oyun yoxdur"}</p>}
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:gap-8" data-testid="games-grid">
          {list.map((g, i) => <GameCard key={g.id} game={g} index={i} />)}
        </div>
      </section>
      {user === false && <HowTo />}
      <section id="payments" className={`max-w-7xl mx-auto px-4 sm:px-8 grid md:grid-cols-3 gap-6 ${user === false ? "pt-12 pb-8" : ""}`}>
        {FEATURES.map(({ Icon, t, d }) => (
          <div key={t} className="rounded-2xl border border-white/10 bg-[#111] p-8 transition-colors hover:border-[#D4AF37]/50">
            <Icon className="text-[#D4AF37] mb-5" size={28} />
            <h3 className="font-display font-semibold text-lg">{t}</h3>
            <p className="text-zinc-400 text-sm mt-2">{d}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
